/**
 * Create User (Super Admin / Tenant Admin)
 *
 * Create a user with email and password. Optionally assign to tenant/workspace.
 * Uses the create-user Edge Function (Supabase Auth + public.users).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useUserStore } from '../../stores/prioritization/userStore';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { createUserWithPassword } from '../../lib/createUserService';

interface TenantOption {
  id: string;
  name: string;
  slug: string;
}

interface WorkspaceOption {
  id: string;
  name: string;
  tenant_id: string;
}

export default function CreateUser() {
  const navigate = useNavigate();
  const currentUserId = useUserStore((s) => s.userId);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [role, setRole] = useState<'member' | 'workspace-admin' | 'tenant-admin'>('member');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ email: string } | null>(null);

  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);

  useEffect(() => {
    const supabase = getSupabaseClient();
    supabase.from('tenants').select('id, name, slug').order('name').then(({ data }) => setTenants(data || []));
    supabase.from('workspaces').select('id, name, tenant_id').order('name').then(({ data }) => setWorkspaces(data || []));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      setError('Email and password are required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setCreating(true);
    try {
      await createUserWithPassword({
        email: trimmedEmail,
        password,
        name: name.trim() || undefined,
        tenantId: tenantId || undefined,
        workspaceId: workspaceId || undefined,
        role: role,
        createdBy: currentUserId || undefined,
      });
      setSuccess({ email: trimmedEmail });
      setName('');
      setEmail('');
      setPassword('');
      setTenantId('');
      setWorkspaceId('');
      setRole('member');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  if (success) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-sm p-8 text-center">
          <div className="p-4 bg-[var(--green-dim)] rounded-full w-fit mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-[var(--green)]" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">User created</h2>
          <p className="text-[var(--text-sec)] mb-4">
            <strong>{success.email}</strong> can log in at <strong>/auth/login</strong> with the password you set.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={() => setSuccess(null)}
              className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]"
            >
              Create another
            </button>
            <button
              type="button"
              onClick={() => navigate('/platform-admin/admin-roles')}
              className="px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)]"
            >
              Back to Admin Roles
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-xl mx-auto">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>
      <h1 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Create user</h1>
      <p className="text-[var(--text-ter)] mb-6">
        Create an account with email and password. Assign to a tenant or workspace optionally. New users are members by default; choose an admin role only when needed.
      </p>

      <form onSubmit={handleSubmit} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
            className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            required
            className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Password *</label>
          <div className="flex gap-2">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              minLength={8}
              className="flex-1 px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] border border-[var(--rule)]"
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Assign to tenant (optional)</label>
          <select
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              setWorkspaceId('');
            }}
            className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-red-500"
          >
            <option value="">None</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Assign to workspace (optional)</label>
          <select
            value={workspaceId}
            onChange={(e) => setWorkspaceId(e.target.value)}
            className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-red-500"
          >
            <option value="">None</option>
            {workspaces
              .filter((w) => !tenantId || w.tenant_id === tenantId)
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
          </select>
        </div>
        {(tenantId || workspaceId) ? (
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'member' | 'workspace-admin' | 'tenant-admin')}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-red-500"
            >
              <option value="member">Member (default)</option>
              {workspaceId && <option value="workspace-admin">Workspace admin</option>}
              {tenantId && <option value="tenant-admin">Tenant admin</option>}
            </select>
          </div>
        ) : null}
        {error && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[var(--coral)] text-sm bg-[var(--coral-dim)] border border-[var(--coral)] p-3">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
            {error.includes('Edge Function') || error.includes('Deploy the function') ? (
              <div className="text-xs text-[var(--text-sec)] space-y-1">
                <p>From the project root (registry-demo):</p>
                <pre className="bg-[var(--hover-tint)] p-2 rounded overflow-x-auto text-left">
supabase functions deploy create-user
supabase secrets set SUPABASE_SERVICE_ROLE_KEY=your_service_role_key_here
                </pre>
                <p>Get the service role key from Supabase Dashboard → Project Settings → API.</p>
              </div>
            ) : null}
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--coral)] text-white hover:bg-[var(--coral)] disabled:opacity-50"
          >
            <UserPlus className="h-4 w-4" />
            {creating ? 'Creating…' : 'Create user'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
